/** @jsxImportSource @emotion/react */
import "./Allowance.css";
import ReactModal from "react-modal";
import { MODAL_SELECTORS } from "../modalSelectors";
import React, { memo, useCallback, useEffect, useState } from "react";
import { GlobalStore } from "../../../../store/global/GlobalStore";
import { ReactComponent as SafeIcon } from "../../../../img/approval_svgs/Safe.svg";
import { Token } from "../../../../store/global/TokenEnum";
import { getAllowances } from "../../../../store/global/getAllowances";
import { useTimedQuoteRefetch } from "../../../../hooks/useTimedQuoteRefetch";
import { OrderStatus } from "../../../../types/orderStatus";
import { getTokenIcon } from "../../../../utils/tokenIconUtils";
import { ModalContent } from "../../../general/ModalContent/ModalContent";
import {
  modalBodyTextStyle,
  revokeAndApprovalSubtitleStyle,
  revokeAndApprovalTextStyle,
} from "./Approvals.style";
import { HorizontalLine } from "./elements/HorizontalLine";
import { Spacer } from "../../../general/Spacer/Spacer";
import { Text } from "../../../general/Text";
import { css } from "@emotion/react";
import { useAccount } from "wagmi";
import SignatureBenefitsSection from "./SignatureBenefitsSection";
import SignatureRequest from "./SignatureRequest";
import ApprovalsBenefitsSection from "./ApprovalsBenefitsSection";
import { ReactComponent as CloseIcon } from "../../../../img/close.svg";
import { ModalCloseButton } from "../SelectTokenModal/TokenSelectModal";
import MetaMaskTip from "./elements/MetaMaskTip";
import { ApprovalRow } from "./ApproveFlow/ApprovalRow";
import { ApprovalStore } from "../../../../store/approvals/ApprovalsStore";

const HELP_LINK = "https://help.bebop.xyz/en";
const APPROVALS_MODAL_CLOSE_TIMEOUT = 2000;
const REVOKE_TEXT = `Your token spend limit is too low. Increasing the spend limit is not supported by the token, so please revoke your current allowance and then make a new approval.`;

const Approvals: React.FC<{
  modalSelector: string | undefined;
}> = ({ modalSelector }) => {
  const { tokensToApprove, requiredSignatures, permitType, tokenMetadata } = ApprovalStore.useState(
    s => s
  );
  const { toApprove, toRevoke } = ApprovalStore.useState(s => {
    return {
      toApprove: s.tokensByFlow.approve,
      toRevoke: s.tokensByFlow.revoke,
    };
  });
  // Seed the initial state from the hook to prevent re-renders.
  const { theme } = GlobalStore.getRawState();
  const { connector } = useAccount();
  const { forceRefresh: forceRefreshQuote } = useTimedQuoteRefetch();
  console.log(tokensToApprove);
  console.log(requiredSignatures);

  const isLoading = false;
  const [isDone, setIsDone] = useState<Array<Record<Token, boolean>>>([]);

  const startAutoCloseTimer = useCallback(async () => {
    console.log(`Starting approvals auto close timer`);
    const address = GlobalStore.getRawState().address;
    await getAllowances.run({ tokenMetadata, address: address as `0x${string}` });
    await forceRefreshQuote();
    setTimeout(() => {
      GlobalStore.update(s => {
        if (s.modalSelector === MODAL_SELECTORS.APPROVE_ALLOWANCE) {
          s.orderStatus = OrderStatus.REVIEW;
          s.modalSelector = undefined;
        }
      });
    }, APPROVALS_MODAL_CLOSE_TIMEOUT);
  }, [forceRefreshQuote, tokenMetadata]);

  const handleClose = useCallback(() => {
    console.log(`B, CLOSING`);
    const areAllApproved = isDone.length === toApprove.length + toRevoke.length;
    const isSignatureRequired = requiredSignatures.length > 0;
    const hasSignatures = isSignatureRequired
      ? GlobalStore.getRawState().permits.permit2.signature ||
        GlobalStore.getRawState().permits.permit1.signature
      : true;
    console.log(`hasSignatures`, hasSignatures);
    /**
     * Initially the tokens to approve are empty, so we need to check if the state matches the props
     * in order to make sure everything was approved.
     */
    const isStateMatchingApprovalProps =
      tokensToApprove.length > 0 ? areAllApproved : areAllApproved;
    if (isStateMatchingApprovalProps) {
      if (permitType !== "permit2" && hasSignatures) {
        void startAutoCloseTimer();
        return;
      }
    }
  }, [
    isDone.length,
    toApprove.length,
    toRevoke.length,
    requiredSignatures.length,
    tokensToApprove.length,
    permitType,
    startAutoCloseTimer,
  ]);
  // Special case for checking if we need to request a signature before close
  useEffect(() => {
    if (
      isDone.length === toApprove.length + toRevoke.length &&
      requiredSignatures.length > 0 &&
      !isLoading &&
      toApprove.length < 1 &&
      toRevoke.length < 1
    ) {
      // Check if the signature length matches
      const needsSignature = requiredSignatures.length;
      const hasSignature =
        permitType === "permit1"
          ? GlobalStore.getRawState().permits.permit1.signature
          : GlobalStore.getRawState().permits.permit2.signature;
      if ((needsSignature && hasSignature) || !needsSignature) handleClose();
    }
  }, [
    handleClose,
    isDone.length,
    isLoading,
    permitType,
    requiredSignatures.length,
    toApprove.length,
    toRevoke.length,
  ]);

  useEffect(() => {
    const areAllApproved = isDone.length === toApprove.length + toRevoke.length;
    if (areAllApproved && !isLoading) {
      handleClose();
    }
  }, [handleClose, isDone.length, isLoading, toApprove.length, toRevoke.length]);

  const handleForceClose = useCallback(() => {
    //CLose explicitly without finishing approvals
    GlobalStore.update(s => {
      if (s.modalSelector === MODAL_SELECTORS.APPROVE_ALLOWANCE) {
        s.modalSelector = undefined;
      }
    });
  }, []);
  const areAllApproved = isDone.length === toApprove.length + toRevoke.length && !isLoading;
  console.log(`isDone`, isDone);
  console.log(`areAllApproved`, areAllApproved);
  const SIGNATURE_REQUEST_TEXT = "Grant permission to securely trade your tokens";
  const MODAL_BODY_LINK_TEXT =
    areAllApproved || permitType === "permit1"
      ? "Read more about token access ›"
      : "Learn more about permissions ›";
  const METAMASK_TIP_TEXT = `Learn more about why we recommend this ›`;
  const isMetamaskTipApplicable =
    connector?.name === "MetaMask" &&
    toApprove.length > 0 &&
    !areAllApproved &&
    permitType === "permit2";
  return (
    <ReactModal
      isOpen={modalSelector === MODAL_SELECTORS.APPROVE_ALLOWANCE}
      className={`react-modal new-skin modal-dialog modal-dialog-centered m-frame approval-modal`}
      style={{
        content: {
          margin: "0 auto",
          marginTop: 80,
          fontSize: "14px",
        },
      }}
      shouldCloseOnOverlayClick={false}
      onRequestClose={() => {
        //CLose explicitly without finishing approvals
        GlobalStore.update(s => {
          if (s.modalSelector === MODAL_SELECTORS.APPROVE_ALLOWANCE) {
            s.modalSelector = undefined;
          }
        });
      }}
    >
      <ModalContent backgroundColor="main">
        <div className={`modal-body`}>
          <div className={"text-end"}>
            {!areAllApproved && (
              <ModalCloseButton
                onClick={() => handleForceClose()}
                css={css`
                  svg {
                    fill: white;
                  }
                `}
              >
                <CloseIcon width={10} height={10} />
              </ModalCloseButton>
            )}
          </div>
          <div className="text-center">
            <SafeIcon />
          </div>
          <Spacer minHeight="16px" />
          <Text
            shouldScale={false}
            textStyle="Title"
            size="Large"
            textCss={css`
              text-align: center;
            `}
          >
            Manage Permissions
          </Text>
          <Spacer minHeight="28px" />
          <MetaMaskTip isVisible={isMetamaskTipApplicable} />
          <Text
            shouldScale={false}
            size="XL"
            textStyle="Paragraph"
            textCss={modalBodyTextStyle(theme)}
            isVisible={!isMetamaskTipApplicable}
          >
            {SIGNATURE_REQUEST_TEXT}
          </Text>
          <a href={HELP_LINK} target={"_blank"} rel="noreferrer">
            <Text
              shouldScale={false}
              size="Large"
              textStyle="Paragraph"
              color={theme.button.tertiary.text.default}
              textCss={css`
                text-align: center;
              `}
              isVisible={!isMetamaskTipApplicable}
            >
              {MODAL_BODY_LINK_TEXT}
            </Text>
          </a>
          <SignatureRequest
            isVisible={
              (areAllApproved || permitType === "permit1") &&
              requiredSignatures.length > 0 &&
              !isLoading
            }
            permitType={permitType}
            onSignatureDone={() => {
              console.log(`Obtained needed signature`);
              void startAutoCloseTimer();
            }}
          />
          <ApprovalsBenefitsSection
            isVisible={
              (areAllApproved && requiredSignatures.length > 0) || permitType === "permit1"
            }
          />
          <SignatureBenefitsSection isVisible={!areAllApproved && permitType !== "permit1"} />
          <a href={HELP_LINK} target={"_blank"} rel="noreferrer">
            <Text
              shouldScale={false}
              size="Large"
              textStyle="Paragraph"
              color={theme.button.tertiary.text.default}
              isVisible={isMetamaskTipApplicable}
              textCss={css`
                text-align: center;
              `}
            >
              {METAMASK_TIP_TEXT}
            </Text>
          </a>
        </div>
        <div className={"approve-allowances-section"}>
          <div
            className="summary"
            style={{
              padding: "17px 17px",
              backgroundColor: theme.surface.main.secondary.default,
              display: !areAllApproved && permitType === "permit2" ? "block" : "none",
            }}
          >
            {permitType && toApprove.length > 0 && !areAllApproved && (
              <>
                {
                  <Text textStyle={"Title"} size={"XS"} shouldScale={false}>
                    Approval Required
                  </Text>
                }
                <Spacer minHeight="12px" />
                {toApprove.map((entry, index) => {
                  const showHorizontalLine = toApprove.length > 1;
                  const lastTokenToApprove = index !== toApprove.length - 1;
                  return (
                    <span key={entry.symbol}>
                      <ApprovalRow
                        symbol={entry.symbol}
                        permitType={permitType}
                        tokenIcon={getTokenIcon(entry.symbol, tokenMetadata)}
                        needed={entry.needed}
                        onStatusChange={newStatus => {
                          console.log(`This should fire`, newStatus);
                          if (newStatus === "DONE") {
                            setIsDone(prev => {
                              return [...prev, { [entry.symbol]: true } as Record<Token, boolean>];
                            });
                          } else if (newStatus === "CUSTOM_APPROVALS") {
                            if (toRevoke.filter(e => e.symbol === entry.symbol).length === 0) {
                              // Check if the token needs to move from approve to revoke
                              ApprovalStore.update(s => {
                                const approve = s.tokensByFlow.approve.filter(
                                  approval => approval.symbol !== entry.symbol
                                );
                                const revoke = s.tokensByFlow.revoke.filter(
                                  revoke => revoke.symbol !== entry.symbol
                                );
                                s.tokensByFlow = {
                                  approve,
                                  revoke: [...revoke, entry],
                                };
                              });
                            }
                          }
                        }}
                      />
                      {showHorizontalLine && lastTokenToApprove ? <HorizontalLine /> : null}
                    </span>
                  );
                })}
                <Spacer minHeight="12px" />
              </>
            )}
            {toRevoke.length > 0 && !areAllApproved && (
              <>
                <div>
                  <p css={revokeAndApprovalSubtitleStyle(toApprove.length > 0)}>
                    Revoke & Approval Required
                  </p>
                  <p className="m-0" css={revokeAndApprovalTextStyle}>
                    {REVOKE_TEXT}
                  </p>
                </div>
                {permitType &&
                  toRevoke.map((entry, index) => {
                    return (
                      <span key={entry.symbol}>
                        {index > 0 ? <hr className="hr-m-15" /> : null}
                        <ApprovalRow
                          symbol={entry.symbol}
                          permitType={permitType}
                          tokenIcon={getTokenIcon(entry.symbol, tokenMetadata)}
                          needed={entry.needed}
                          onStatusChange={newStatus => {
                            if (newStatus === "DONE") {
                              setIsDone(prev => {
                                return [
                                  ...prev,
                                  { [entry.symbol]: true } as Record<Token, boolean>,
                                ];
                              });
                            } else if (newStatus === "CUSTOM_APPROVALS") {
                              if (toRevoke.filter(e => e.symbol === entry.symbol).length === 0) {
                                // Check if the token needs to move from approve to revoke
                                ApprovalStore.update(s => {
                                  const approve = s.tokensByFlow.approve.filter(
                                    approval => approval.symbol !== entry.symbol
                                  );
                                  const revoke = s.tokensByFlow.revoke.filter(
                                    revoke => revoke.symbol !== entry.symbol
                                  );
                                  s.tokensByFlow = {
                                    approve,
                                    revoke: [...revoke, entry],
                                  };
                                });
                              }
                            }
                          }}
                        />
                      </span>
                    );
                  })}
              </>
            )}
          </div>
        </div>
      </ModalContent>
    </ReactModal>
  );
};
Approvals.whyDidYouRender = true;
export default memo(Approvals);
